import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';

interface TieBreakerOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (tieBreakerOrder: string[]) => void;
  initialOrder?: string[];
}

const availableTieBreakerCriteria = [
  { id: 'goal_difference', name: 'Goal Difference', description: 'Difference between goals scored and conceded' },
  { id: 'goals_for', name: 'Goals For', description: 'Total goals scored' },
  { id: 'goals_against', name: 'Goals Against', description: 'Total goals conceded' },
  { id: 'head_to_head', name: 'Head to Head', description: 'Direct match results between teams' },
];

const TieBreakerOrderModal: React.FC<TieBreakerOrderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialOrder = []
}) => {
  const [selectedOrder, setSelectedOrder] = useState<string[]>(initialOrder);
  const [availableCriteria, setAvailableCriteria] = useState<string[]>([]);

  // Initialize available criteria (those not already selected)
  useEffect(() => {
    if (isOpen) {
      setSelectedOrder(initialOrder);
      const allCriteria = availableTieBreakerCriteria.map(c => c.id);
      setAvailableCriteria(allCriteria.filter(c => !initialOrder.includes(c)));
    }
  }, [isOpen, initialOrder]);

  const handleAddCriterion = (criterionId: string) => {
    setSelectedOrder(prev => [...prev, criterionId]);
    setAvailableCriteria(prev => prev.filter(id => id !== criterionId));
  };

  const handleRemoveCriterion = (criterionId: string) => {
    setSelectedOrder(prev => prev.filter(id => id !== criterionId));
    setAvailableCriteria(prev => [...prev, criterionId]);
  };

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      const newOrder = [...selectedOrder];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setSelectedOrder(newOrder);
    }
  };

  const handleMoveDown = (index: number) => {
    if (index < selectedOrder.length - 1) {
      const newOrder = [...selectedOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setSelectedOrder(newOrder);
    }
  };

  const handleSave = () => {
    onSuccess(selectedOrder);
    onClose();
  };

  const getCriterionName = (id: string) => {
    return availableTieBreakerCriteria.find(c => c.id === id)?.name || id;
  };

  const getCriterionDescription = (id: string) => {
    return availableTieBreakerCriteria.find(c => c.id === id)?.description || '';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Set Tie-Breaker Order">
      <div className="p-4">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-4">
            Set the order of tie-breaker criteria. Teams will be ranked first by the top criterion, 
            then by the second criterion if tied, and so on.
          </p>
        </div>

        {/* Selected Order */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Tie-Breaker Order</h3>
          {selectedOrder.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
              <p>No criteria selected</p>
              <p className="text-xs mt-1">Add criteria from the list below</p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedOrder.map((criterionId, index) => (
                <div key={criterionId} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white text-xs font-medium rounded-full flex items-center justify-center">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {getCriterionName(criterionId)}
                      </p>
                      <p className="text-xs text-gray-600">
                        {getCriterionDescription(criterionId)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === selectedOrder.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRemoveCriterion(criterionId)}
                      className="p-1 text-red-400 hover:text-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Available Criteria */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Available Criteria</h3>
          {availableCriteria.length === 0 ? (
            <div className="text-center py-4 text-gray-500 border border-gray-200 rounded-lg">
              <p className="text-sm">All criteria have been added</p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableCriteria.map((criterionId) => (
                <div key={criterionId} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {getCriterionName(criterionId)}
                    </p>
                    <p className="text-xs text-gray-600">
                      {getCriterionDescription(criterionId)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleAddCriterion(criterionId)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            onClick={onClose}
            variant="secondary"
            size="md"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            size="md"
          >
            Save Order
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TieBreakerOrderModal;
